/**
 * Finance Hub - Main Page with Tabs
 * 
 * Phase 5: Company-wide finance management
 * Permissions: Owner/Admin/Finance/Manager only
 */
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LayoutDashboard, FileText, CreditCard, Users, CheckSquare, BookOpen, Book, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';

import FinanceDashboard from './FinanceDashboard';
import InvoicesList from './InvoicesList';
import PaymentsList from './PaymentsList';
import CommissionBatches from './CommissionBatches';
import ApprovalsQueue from './ApprovalsQueue';
import ChartOfAccounts from './ChartOfAccounts';
import JournalEntriesList from './JournalEntriesList';
import FinancialReports from './FinancialReports';

const ALLOWED_ROLES = ['super_admin', 'admin', 'owner', 'finance', 'manager', 'ceo', 'president', 'director_of_sales', 'operations_manager', 'general_manager'];

export default function FinanceHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  // Permission check
  const hasAccess = user && ALLOWED_ROLES.includes(user.role);

  if (!hasAccess) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300">Access Restricted</h2>
            <p className="text-slate-500 mt-2">You do not have permission to view the Finance Hub.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="finance-hub">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Finance Hub</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Company-wide financial management</p>
            </div>
          </div>
        </div>

      {/* Tabs */}
      <div className="px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1 rounded-lg flex-wrap h-auto">
            <TabsTrigger 
              value="dashboard" 
              className="flex items-center gap-2"
              data-testid="tab-dashboard"
            >
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="invoices" 
              className="flex items-center gap-2"
              data-testid="tab-invoices"
            >
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger 
              value="payments" 
              className="flex items-center gap-2"
              data-testid="tab-payments"
            >
              <CreditCard className="h-4 w-4" />
              Payments
            </TabsTrigger>
            <TabsTrigger 
              value="journal" 
              className="flex items-center gap-2"
              data-testid="tab-journal"
            >
              <Book className="h-4 w-4" />
              Journal Entries
            </TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className="flex items-center gap-2"
              data-testid="tab-reports"
            >
              <BarChart3 className="h-4 w-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger 
              value="commissions" 
              className="flex items-center gap-2"
              data-testid="tab-commissions"
            >
              <Users className="h-4 w-4" />
              Commissions
            </TabsTrigger>
            <TabsTrigger 
              value="approvals" 
              className="flex items-center gap-2"
              data-testid="tab-approvals"
            >
              <CheckSquare className="h-4 w-4" />
              Approvals
            </TabsTrigger>
            <TabsTrigger 
              value="accounts" 
              className="flex items-center gap-2"
              data-testid="tab-accounts"
            >
              <BookOpen className="h-4 w-4" />
              Chart of Accounts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4">
            <FinanceDashboard user={user} onNavigate={setActiveTab} />
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <InvoicesList user={user} />
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <PaymentsList user={user} />
          </TabsContent>

          <TabsContent value="journal" className="mt-4">
            <JournalEntriesList user={user} />
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <FinancialReports user={user} />
          </TabsContent>

          <TabsContent value="commissions" className="mt-4">
            <CommissionBatches user={user} />
          </TabsContent>

          <TabsContent value="approvals" className="mt-4">
            <ApprovalsQueue user={user} />
          </TabsContent>

          <TabsContent value="accounts" className="mt-4">
            <ChartOfAccounts user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </Layout>
  );
}
